// src/pages/terminos/TerminosCondiciones.js
import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import { toast } from 'react-toastify'
import api from 'api/api'

import { normalizarBooleano } from 'utils/general'
import TopHeader from 'components/TopHeader'
import { RichTextFieldModal } from 'components/RichText'

import './terminos.css'

function TerminosCondiciones() {
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [configuracion, setConfiguracion] = useState({
		habilitado: false,
		contenido: '',
		fechaModificacion: null
	})
	const [contenidoOriginal, setContenidoOriginal] = useState('') // Valor original del servidor
	const [contenidoEditado, setContenidoEditado] = useState('')
	const [hayCambios, setHayCambios] = useState(false)
	const [editorKey, setEditorKey] = useState(0) // Key para forzar re-render del editor

	// Cargar configuración
	const refreshConfiguracion = async () => {
		try {
			setLoading(true)
			const { datos } = await api.get('p/config-terminos')
			setConfiguracion({
				...datos,
				habilitado: normalizarBooleano(datos.habilitado)
			})
			const contenido = datos.contenido || ''
			setContenidoOriginal(contenido) // Guardar el original del servidor
			setContenidoEditado(contenido)
		} catch (error) {
			toast.error('Error al cargar configuración')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		refreshConfiguracion()
	}, [])

	// Detectar cambios comparando con el original del servidor
	useEffect(() => {
		const cambioContenido = contenidoEditado !== contenidoOriginal
		setHayCambios(cambioContenido)
	}, [contenidoEditado, contenidoOriginal])

	// Toggle habilitado/deshabilitado
	const toggleHabilitado = async (nuevoValor) => {
		try {
			const { msg, datos } = await api.put('a/config-terminos', { habilitado: nuevoValor })
			setConfiguracion(prev => ({ ...prev, habilitado: nuevoValor }))
			toast.success(msg || 'Configuración actualizada')
		} catch (error) {
			toast.error('Error al actualizar configuración')
			console.error(error)
		}
	}

	// Guardar contenido
	const guardarContenido = async () => {
		try {
			setSaving(true)
			const { msg, datos } = await api.put('a/config-terminos', {
				contenido: contenidoEditado
			})
			setConfiguracion(datos)
			setContenidoOriginal(contenidoEditado) // Actualizar el original después de guardar exitosamente
			setHayCambios(false)
			toast.success(msg || 'Contenido guardado correctamente')
		} catch (error) {
			toast.error('Error al guardar el contenido')
			console.error(error)
		} finally {
			setSaving(false)
		}
	}

	// Descartar cambios - volver al valor original del servidor
	const descartarCambios = () => {
		setContenidoEditado(contenidoOriginal)
		setEditorKey(prev => prev + 1) // Incrementar key para forzar re-render
		setHayCambios(false)
		toast.info('Cambios descartados')
	}

	// Formatear fecha
	const formatearFecha = (fechaString) => {
		if (!fechaString) return 'Nunca'
		const fecha = new Date(fechaString)
		return fecha.toLocaleDateString('es-ES', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		})
	}

	// render
	return (
		<Container fluid id="terminos-condiciones" className="vh-100">
			<TopHeader
				iconName="file-contract"
				text="Términos y Condiciones"
			/>

			{loading ? (
				<div className="text-center py-5">
					<Spinner animation="border" variant="primary" />
					<p className="mt-3">Cargando...</p>
				</div>
			) : (
				<>
					{/* Switch principal para habilitar/deshabilitar la sección */}
					<Row className="mb-4">
						<Col>
							<Alert
								variant={configuracion.habilitado ? "success" : "warning"}
								className="d-flex align-items-center justify-content-between"
							>
								<div>
									<i className={`fa ${configuracion.habilitado ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
									<strong>Estado de la sección:</strong>{' '}
									{configuracion.habilitado
										? 'Habilitada en el sitio web'
										: 'Deshabilitada (no visible en el sitio web)'
									}
								</div>
								<Form.Check
									type="switch"
									id="switch-habilitado"
									label={"Activo"}
									checked={configuracion.habilitado}
									onChange={(e) => toggleHabilitado(e.target.checked)}
								/>
							</Alert>
						</Col>
					</Row>

					{/* Información de última modificación */}
					<Row className="mb-3">
						<Col>
							<small className="text-muted">
								<i className="fa fa-clock me-1"></i>
								Última modificación: {formatearFecha(configuracion.fechaModificacion)}
							</small>
						</Col>
					</Row>

					{/* Editor de contenido */}
					<Row>
						<Col lg={12}>
							<Card className="shadow-sm">
								<Card.Header className="bg-light d-flex justify-content-between align-items-center">
									<h5 className="mb-0">
										<i className="fa fa-edit me-2"></i>
										Contenido de Términos y Condiciones
									</h5>
									{hayCambios && (
										<span className="badge bg-warning text-dark">
											<i className="fa fa-exclamation-circle me-1"></i>
											Cambios sin guardar
										</span>
									)}
								</Card.Header>
								<Card.Body>
									<RichTextFieldModal
										key={editorKey}
										value={contenidoEditado}
										onChange={setContenidoEditado}
										label=""
									/>

									{!contenidoEditado && (
										<Alert variant="info" className="mt-3">
											<i className="fa fa-info-circle me-2"></i>
											No hay contenido aún. Haz clic en "Modificar" para agregar los términos y condiciones.
										</Alert>
									)}
								</Card.Body>
							</Card>
						</Col>
					</Row>

					{/* Botones de acción */}
					<Row className="mt-4 mb-4">
						<Col className="d-flex justify-content-end gap-2">
							{hayCambios && (
								<Button
									variant="outline-secondary"
									onClick={descartarCambios}
									disabled={saving}
								>
									<i className="fa fa-times me-2"></i>
									Descartar cambios
								</Button>
							)}
							<Button
								variant="primary"
								onClick={guardarContenido}
								disabled={saving || !hayCambios}
							>
								{saving ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" />
										Guardando...
									</>
								) : (
									<>
										<i className="fa fa-save me-2"></i>
										Guardar cambios
									</>
								)}
							</Button>
						</Col>
					</Row>
				</>
			)}
		</Container>
	)
}

export default TerminosCondiciones